<!--
  Dashboard — admin portal landing (redesigned 2026-05-25).

  Structure:
    1. Sticky header.
    2. 🔎 Cerca ordini — filter + search bar.
    3. 📋 Operazioni in corso — 6 phase counters (all-time).
    4. 🎯 Obiettivi → Oggi — 4 FIXED cards (current month / today).
    5. 📅 Riepilogo storico — 2 cards driven by month / date pickers.

  Rule: the "Oggi" subsection is anchored to TODAY's calendar and
  doesn't react to the historical pickers. The historical pickers
  don't write to goalService.
-->

<div class="dashboard-page">

  <!-- ─── Sticky header ─── -->
  <header class="dashboard-header">
    <div class="header-titles">
      <h1 class="dashboard-title">Dashboard</h1>
      <p class="dashboard-subtitle">Panoramica operazioni in corso e obiettivi commerciali</p>
    </div>
  </header>

  <hr class="dashboard-divider" />

  <!-- ─── 🔎 Cerca ordini ─── -->
  <h2 class="section-heading">🔎 Cerca ordini</h2>

  <section class="search-card" aria-label="Filtri di ricerca">
    <div class="search-toggles">
      <button type="button" class="search-toggle"
              [class.toggle-on]="searchMode === 'order'"
              (click)="toggleSearchMode('order')">
        <i class="bx" [class.bx-circle]="searchMode !== 'order'" [class.bx-check-circle]="searchMode === 'order'"></i>
        <span>Ordine</span>
      </button>
      <button type="button" class="search-toggle"
              [class.toggle-on]="searchMode === 'cliente'"
              (click)="toggleSearchMode('cliente')">
        <i class="bx" [class.bx-circle]="searchMode !== 'cliente'" [class.bx-check-circle]="searchMode === 'cliente'"></i>
        <span>Cliente</span>
      </button>
      <button type="button" class="search-toggle"
              [class.toggle-on]="searchMode === 'notes'"
              (click)="toggleSearchMode('notes')">
        <i class="bx" [class.bx-circle]="searchMode !== 'notes'" [class.bx-check-circle]="searchMode === 'notes'"></i>
        <span>Riferimento</span>
      </button>
      <button type="button" class="search-toggle"
              [class.toggle-on]="searchMode === 'tracking'"
              (click)="toggleSearchMode('tracking')">
        <i class="bx" [class.bx-circle]="searchMode !== 'tracking'" [class.bx-check-circle]="searchMode === 'tracking'"></i>
        <span>Tracking</span>
      </button>
    </div>

    <div class="search-controls">
      <div class="search-field">
        <label for="search-from">Dal</label>
        <kendo-datepicker
          id="search-from"
          [value]="endDate"
          (valueChange)="endDate = $event; updateFormattedDate()"
          class="search-datepicker"
        ></kendo-datepicker>
      </div>

      <div class="search-field search-input-field">
        <label for="search-input">Cerca</label>
        <input
          id="search-input"
          type="text"
          [(ngModel)]="search"
          placeholder="Inserisci il termine di ricerca…"
          class="search-input"
        />
      </div>

      <button
        type="button"
        class="search-submit"
        [disabled]="!searchType || !search"
        (click)="searchInfoOrders(formattedDate, search)"
      >
        <i class="bx bx-search"></i>
        <span>Cerca</span>
      </button>
    </div>
  </section>

  <!-- ─── 📋 Operazioni in corso ─── -->
  <h2 class="section-heading">📋 Operazioni in corso</h2>

  @if (loading && !firstEnteredOnPage) {
    <section class="ops-grid">
      @for (i of [1,2,3,4,5,6]; track i) {
        <div class="skeleton skeleton-op-card"></div>
      }
    </section>
  } @else {
    <section class="ops-grid" aria-label="Conteggi degli ordini per fase">

      <article class="op-card op-blue">
        <header class="op-card-header">
          <i class="bx bxs-box op-icon"></i>
          <span class="op-label">Ordini da assegnare</span>
        </header>
        <div class="op-value">{{ orderInfo.t_or_new === -1 ? '—' : orderInfo.t_or_new }}</div>
        @if (orderInfo.t_or_new !== -1 && orderInfo.t_or_new !== 0) {
          <a class="op-link" routerLink="/assign-order" [queryParams]="dynamicLink">
            Vai <i class="bx bx-right-arrow-alt"></i>
          </a>
        } @else if (orderInfo.t_or_new === 0) {
          <span class="op-empty">Nessun ordine</span>
        }
      </article>

      <article class="op-card op-purple">
        <header class="op-card-header">
          <i class="bx bxs-package op-icon"></i>
          <span class="op-label">Ordini da preparare</span>
        </header>
        <div class="op-value">{{ orderInfo.t_or_prepare === -1 ? '—' : orderInfo.t_or_prepare }}</div>
        @if (orderInfo.t_or_prepare !== -1 && orderInfo.t_or_prepare !== 0) {
          <a class="op-link" routerLink="/prepare-order" [queryParams]="dynamicLink">
            Vai <i class="bx bx-right-arrow-alt"></i>
          </a>
        } @else if (orderInfo.t_or_prepare === 0) {
          <span class="op-empty">Nessun ordine</span>
        }
      </article>

      <article class="op-card op-yellow">
        <header class="op-card-header">
          <i class="bx bxs-moon op-icon"></i>
          <span class="op-label">Ordini parziali</span>
        </header>
        <div class="op-value">{{ orderInfo.t_or_partial === -1 ? '—' : orderInfo.t_or_partial }}</div>
        @if (orderInfo.t_or_partial !== -1 && orderInfo.t_or_partial !== 0) {
          <a class="op-link" routerLink="/partial-orders" [queryParams]="dynamicLink">
            Vai <i class="bx bx-right-arrow-alt"></i>
          </a>
        } @else if (orderInfo.t_or_partial === 0) {
          <span class="op-empty">Nessun ordine</span>
        }
      </article>

      <article class="op-card op-teal">
        <header class="op-card-header">
          <i class="bx bxs-star op-icon"></i>
          <span class="op-label">Ordini PM</span>
        </header>
        <div class="op-value">{{ orderInfo.t_or_cm === -1 ? '—' : orderInfo.t_or_cm }}</div>
        @if (orderInfo.t_or_cm !== -1 && orderInfo.t_or_cm !== 0) {
          <a class="op-link" routerLink="/pm-orders" [queryParams]="dynamicLink">
            Vai <i class="bx bx-right-arrow-alt"></i>
          </a>
        } @else if (orderInfo.t_or_cm === 0) {
          <span class="op-empty">Nessun ordine</span>
        }
      </article>

      <article class="op-card op-coral">
        <header class="op-card-header">
          <i class="bx bxs-edit op-icon"></i>
          <span class="op-label">Ordini da firmare</span>
        </header>
        <div class="op-value">{{ orderInfo.t_or_to_sign === -1 ? '—' : orderInfo.t_or_to_sign }}</div>
        @if (orderInfo.t_or_to_sign !== -1 && orderInfo.t_or_to_sign !== 0) {
          <a class="op-link" routerLink="/firmare" [queryParams]="dynamicLink">
            Vai <i class="bx bx-right-arrow-alt"></i>
          </a>
        } @else if (orderInfo.t_or_to_sign === 0) {
          <span class="op-empty">Nessun ordine</span>
        }
      </article>

      <article class="op-card op-green">
        <header class="op-card-header">
          <i class="bx bxs-check-circle op-icon"></i>
          <span class="op-label">Ordini completati</span>
        </header>
        <div class="op-value">{{ orderInfo.t_or_completed === -1 ? '—' : orderInfo.t_or_completed }}</div>
        @if (orderInfo.t_or_completed !== -1 && orderInfo.t_or_completed !== 0) {
          <a class="op-link" routerLink="/history" [queryParams]="dynamicLink">
            Vai <i class="bx bx-right-arrow-alt"></i>
          </a>
        } @else if (orderInfo.t_or_completed === 0) {
          <span class="op-empty">Nessun ordine</span>
        }
      </article>

    </section>
  }

  <!-- ─── 🎯 Obiettivi → Oggi ─── -->
  <h2 class="section-heading">🎯 Obiettivi · Oggi</h2>
  <p class="section-hint">Stato del mese corrente e di oggi. Indipendente dai filtri storici qui sotto.</p>

  <section class="goals-grid goals-grid-today" aria-label="Obiettivi correnti">

    <!-- Card 1: Obiettivo del mese (corrente) -->
    <article class="goal-card goal-blue">
      <header class="goal-card-header">
        <span class="goal-label">Obiettivo del mese ({{ months[todayMonth].name }})</span>
        <i class="bx bx-target-lock goal-icon"></i>
      </header>
      <div class="goal-value">{{ currentMonthGoal | currency : 'EUR' : 'symbol' : '1.2-2' }}</div>
      <div class="goal-progress">
        <div class="progress-track">
          <div class="progress-fill"
               [class.pulse-glow]="currentMonthProgress >= 100"
               [style.width.%]="currentMonthProgress > 100 ? 100 : currentMonthProgress"></div>
        </div>
        <span class="progress-label">
          {{ currentMonthProgress | number : '1.0-0' }}% completato · 💼
          {{ currentMonthEarned | currency : 'EUR' : 'symbol' : '1.2-2' }} guadagnati
        </span>
      </div>
      @if (currentMonthGoal > 0) {
        <p class="goal-motivation">{{ getMonthlyMotivationalMessage() }}</p>
      } @else {
        <p class="goal-motivation">Imposta un obiettivo dalle Impostazioni per attivare il monitoraggio.</p>
      }
    </article>

    <!-- Card 2: Obiettivo di oggi (dinamico) -->
    <article class="goal-card goal-purple">
      <header class="goal-card-header">
        <span class="goal-label">Obiettivo di oggi</span>
        <i class="bx bx-time goal-icon"></i>
      </header>
      @if (todayDailyGoal > 0) {
        <div class="goal-value">{{ todayDailyGoal | currency : 'EUR' : 'symbol' : '1.2-2' }}</div>
        <div class="goal-progress">
          <div class="progress-track">
            <div class="progress-fill"
                 [class.pulse-glow]="todayDailyProgress >= 100"
                 [style.width.%]="todayDailyProgress > 100 ? 100 : todayDailyProgress"></div>
          </div>
          <span class="progress-label">
            {{ todayDailyProgress | number : '1.0-0' }}% · 💰
            {{ todayEarned | currency : 'EUR' : 'symbol' : '1.2-2' }} oggi
          </span>
        </div>
        <p class="goal-motivation">{{ getDailyMotivationalMessage() }}</p>
      } @else {
        <div class="goal-value goal-value-empty">✅ Goditi la giornata!</div>
        <p class="goal-motivation">Obiettivo mensile già raggiunto (o non impostato)</p>
      }
    </article>

    <!-- Card 5: Forecast a fine mese -->
    <article class="goal-card goal-yellow">
      <header class="goal-card-header">
        <span class="goal-label">Forecast a fine mese</span>
        <i class="bx bx-trending-up goal-icon"></i>
      </header>
      <div class="goal-value">{{ forecastEndOfMonth | currency : 'EUR' : 'symbol' : '1.0-0' }}</div>
      <div class="goal-progress">
        <div class="progress-track">
          <div class="progress-fill"
               [style.width.%]="forecastPercent > 100 ? 100 : forecastPercent"></div>
        </div>
        <span class="progress-label">
          {{ forecastPercent | number : '1.0-0' }}% dell'obiettivo
        </span>
      </div>
      @if (currentMonthGoal > 0) {
        <p class="goal-motivation forecast-status forecast-{{ forecastStatus }}">
          @if (forecastStatus === 'good') {
            🟢 Pace sopra il piano — fine mese sopra l'obiettivo.
          } @else if (forecastStatus === 'warning') {
            🟡 Pace appena sotto — fattibile, ma serve un'accelerata.
          } @else {
            🔴 Pace troppo basso — al ritmo attuale non si arriva all'obiettivo.
          }
        </p>
      } @else {
        <p class="goal-motivation">Imposta un obiettivo mensile per calcolare il forecast.</p>
      }
    </article>

    <!-- Card 6: Year-to-date -->
    <article class="goal-card goal-green">
      <header class="goal-card-header">
        <span class="goal-label">Year-to-date ({{ todayYear }})</span>
        <i class="bx bx-calendar-check goal-icon"></i>
      </header>
      <div class="goal-value">{{ ytdEarned | currency : 'EUR' : 'symbol' : '1.0-0' }}</div>
      <div class="goal-progress">
        <div class="progress-track">
          <div class="progress-fill"
               [style.width.%]="ytdPercent > 100 ? 100 : ytdPercent"></div>
        </div>
        <span class="progress-label">
          {{ ytdPercent | number : '1.0-0' }}% dell'obiettivo annuale ({{ ytdYearlyGoal | currency : 'EUR' : 'symbol' : '1.0-0' }})
        </span>
      </div>
      <p class="goal-motivation">Totale evaso da gennaio a oggi.</p>
    </article>

  </section>

  <!-- ─── 📅 Riepilogo storico ─── -->
  <h2 class="section-heading">📅 Riepilogo storico</h2>
  <p class="section-hint">Lookup di mesi e giornate passate. Non influisce sui KPI sopra.</p>

  <section class="goals-grid goals-grid-history" aria-label="Riepilogo storico">

    <!-- Card 3: Riepilogo mensile -->
    <article class="goal-card goal-neutral">
      <header class="goal-card-header">
        <span class="goal-label">Riepilogo mensile</span>
        <i class="bx bx-calendar goal-icon"></i>
      </header>
      <div class="goal-pickers">
        <select [(ngModel)]="selectedMonth" (change)="onMonthChange()" class="goal-select">
          @for (m of months; track m.value) {
            <option [ngValue]="m.value">{{ m.name }}</option>
          }
        </select>
        <select [(ngModel)]="selectedYear" (change)="onMonthChange()" class="goal-select">
          @for (y of years; track y) {
            <option [ngValue]="y">{{ y }}</option>
          }
        </select>
      </div>
      <div class="goal-meta-label">{{ selectedMonthLabel }}</div>
      <div class="goal-value goal-value-secondary">
        {{ selectedMonthEarned | currency : 'EUR' : 'symbol' : '1.2-2' }}
      </div>
      <p class="goal-meta">Totale evaso nel mese selezionato</p>
    </article>

    <!-- Card 4: Riepilogo giornaliero -->
    <article class="goal-card goal-neutral">
      <header class="goal-card-header">
        <span class="goal-label">Riepilogo giornaliero</span>
        <i class="bx bx-calendar-event goal-icon"></i>
      </header>
      <div class="goal-pickers">
        <input
          type="date"
          [(ngModel)]="selectedDate"
          (change)="onDateChange()"
          class="goal-date-input"
        />
      </div>
      <div class="goal-meta-label">
        {{ selectedDate ? (selectedDate | date : 'dd/MM/yyyy') : '—' }}
      </div>
      <div class="goal-value goal-value-secondary">
        {{ selectedDateEarned | currency : 'EUR' : 'symbol' : '1.2-2' }}
      </div>
      <p class="goal-meta">Totale evaso nella data selezionata</p>
    </article>

  </section>
</div>
